import { UpdatePostDto } from '@/schemas';
import type {
  FormatPostDto,
  FormatPostSuccessResponse,
  GetFormattedPostSuccessResponse,
  GetFormattedPutResponse,
  GetPostsSuccessResponse,
  GetPostSuccessResponse,
  SocialChannels,
  UpdatePostSuccessResponse,
} from '@/types';
import axios, { AxiosError } from 'axios';
import {
  FacebookPublishRequest,
  InstagramPublishRequest,
  LinkedInPublishRequest,
  PublishPostResponse,
} from '@/types/posts';
import { SocialMediaType } from '@/types/settings.ts';

import axiosInstance from './axiosInstance';

export const getPost = (
  projectId: string,
  postId: string
): Promise<GetPostSuccessResponse> =>
  axiosInstance.get(`/projects/${projectId}/posts/${postId}`);

export const getPosts = (
  projectId: string,
  page_size: number = 10,
  page: number = 1
): Promise<GetPostsSuccessResponse> =>
  axiosInstance.get(`/projects/${projectId}/posts`, {
    params: {
      page_size,
      page,
    },
  });

export const formatPost = ({
  formats,
  postId,
  projectId,
}: FormatPostDto): Promise<FormatPostSuccessResponse> =>
  axiosInstance.post(`/projects/${projectId}/posts/${postId}/formatted`, {
    formats,
  });

export const getFormattedPosts = (
  projectId: string,
  postId: string,
  postFormat: SocialChannels
): Promise<GetFormattedPostSuccessResponse> =>
  axiosInstance.get(
    `/projects/${projectId}/posts/${postId}/formatted/${postFormat}`
  );

export const updatePost = (
  projectId: string,
  postId: string,
  body: UpdatePostDto
): Promise<UpdatePostSuccessResponse> =>
  axiosInstance.put(`/projects/${projectId}/posts/${postId}`, body);

export interface IAttachImageToPostResponse {
  success?: boolean;
  error?: string;
}

export const deletePostImage = async (
  project_id: string,
  post_id: string,
  image_id: string
): Promise<boolean | AxiosError> => {
  return axiosInstance
    .delete(`/projects/${project_id}/posts/${post_id}/images/${image_id}`)
    .then(() => {
      return true;
    })
    .catch((err) => {
      return err;
    });
};

export const attachImageToPost = async (
  projectId: string,
  postId: string,
  file: File
): Promise<IAttachImageToPostResponse> => {
  const presignedUrl = await axiosInstance
    .post(`/projects/${projectId}/posts/${postId}/images`, {
      filename: file.name,
    })
    .then((res) => res?.data?.upload_url || '');

  if (presignedUrl) {
    const result: IAttachImageToPostResponse = await new Promise(
      (resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener('load', async () => {
          const response = await axios({
            url: presignedUrl,
            method: 'put',
            data: reader.result,
            headers: { 'Content-Type': file.type },
            timeout: 3 * 60 * 1000, //3mins
          }).catch((error) => reject({ error }));

          return resolve({ success: response?.status === 200 });
        });
        reader.readAsArrayBuffer(file);
      }
    );
    return result;
  }

  return { error: 'something went wrong on image uploading' };
};

/**
 * Publishes a post to Instagram
 */
export const publishToInstagram = (
  projectId: string,
  postId: string,
  data: InstagramPublishRequest
): Promise<PublishPostResponse> =>
  axiosInstance.post(
    `/projects/${projectId}/posts/${postId}/formatted/instagram/publish`,
    data
  );

/**
 * Publishes a post to Facebook
 */
export const publishToFacebook = (
  projectId: string,
  postId: string,
  data: FacebookPublishRequest
): Promise<PublishPostResponse> =>
  axiosInstance.post(
    `/projects/${projectId}/posts/${postId}/formatted/facebook/publish`,
    data
  );

/**
 * Publishes a post to LinkedIn
 */
export const publishToLinkedIn = (
  projectId: string,
  postId: string,
  data: LinkedInPublishRequest
): Promise<PublishPostResponse> =>
  axiosInstance.post(
    `/projects/${projectId}/posts/${postId}/formatted/linked_in/publish`,
    data
  );

/**
 * Generic function to publish to any supported social media platform
 */
export const publishToSocialMedia = (
  projectId: string,
  postId: string,
  socialChannel: SocialMediaType,
  data:
    | InstagramPublishRequest
    | FacebookPublishRequest
    | LinkedInPublishRequest
): Promise<PublishPostResponse> => {
  switch (socialChannel) {
    case SocialMediaType.Instagram:
      return publishToInstagram(
        projectId,
        postId,
        data as InstagramPublishRequest
      );
    case SocialMediaType.Facebook:
      return publishToFacebook(
        projectId,
        postId,
        data as FacebookPublishRequest
      );
    case SocialMediaType.Linkedin:
      return publishToLinkedIn(
        projectId,
        postId,
        data as LinkedInPublishRequest
      );
    default:
      throw new Error(`Unsupported social channel: ${socialChannel}`);
  }
};

export const updateFormattedPost = (
  projectId: string,
  postId: string,
  format: string,
  content: string
): Promise<GetFormattedPutResponse> =>
  axiosInstance.put(
    `/projects/${projectId}/posts/${postId}/formatted/${format}`,
    {
      content,
    }
  );
